<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import DateRangePicker from './components/DateRangePicker.vue';
import KanbanFilterOperatorSelect from './components/KanbanFilterOperatorSelect.vue';
import KanbanFilterDateRangeInput from './components/KanbanFilterDateRangeInput.vue';
import { useNativeFieldOptions } from 'dashboard/composables/useNativeFieldOptions';
import { useMultiSelectLabel } from 'dashboard/composables/useMultiSelectLabel';

const props = defineProps({
  selectedTags: { type: Array, default: () => [] },
  allLabels: { type: Array, default: () => [] },
  selectedSources: { type: Array, default: () => [] },
  selectedProducts: { type: Array, default: () => [] },
  selectedCities: { type: Array, default: () => [] },
  selectedWatcherIds: { type: Array, default: () => [] },
  initialValueMin: { type: String, default: '' },
  initialValueMax: { type: String, default: '' },
  initialClosingDateFrom: { type: String, default: '' },
  initialClosingDateTo: { type: String, default: '' },
  initialDateFrom: { type: String, default: '' },
  initialDateTo: { type: String, default: '' },
  initialDateField: { type: String, default: 'created_at' },
});

const emit = defineEmits(['filterChange', 'close']);

const store = useStore();
const { t } = useI18n();

const activePipelineCategory = computed(
  () => store.getters['kanban/activePipeline']?.category ?? 'sales'
);

const opportunityFolders = computed(() =>
  store.getters['kanbanFolders/folders'].filter(
    f => !f.category || f.category === activePipelineCategory.value
  )
);

onMounted(() => {
  if (!opportunityFolders.value.length) {
    store.dispatch('kanbanFolders/fetchFolders');
  }
});

// Helpers para suportar attribute_values como [{label, key}] ou string legada
function optKey(opt) {
  return typeof opt === 'object' ? opt.key : opt;
}
function optLabel(opt) {
  return typeof opt === 'object' ? opt.label : opt;
}

const opportunityAttributes = computed(() =>
  store.getters['attributes/getAttributesByModel']('opportunity_attribute').map(
    camelcaseKeys
  )
);
const contactAttributes = computed(() =>
  store.getters['attributes/getAttributesByModel']('contact_attribute').map(
    camelcaseKeys
  )
);
const conversationAttributes = computed(() =>
  store.getters['attributes/getAttributesByModel'](
    'conversation_attribute'
  ).map(camelcaseKeys)
);

// ── controle de abertura dos dropdowns de seleção múltipla ───────────────────
const showTagMenu = ref(false);
const showSourceMenu = ref(false);
const showProductMenu = ref(false);
const showCityMenu = ref(false);
const showWatcherMenu = ref(false);

function closeMenus() {
  showTagMenu.value = false;
  showSourceMenu.value = false;
  showProductMenu.value = false;
  showCityMenu.value = false;
  showWatcherMenu.value = false;
}

// ── etiquetas ──────────────────────────────────────────────────────────────
const localTags = ref([...props.selectedTags]);
const tagSearch = ref('');
const tagHighlight = ref(0);
const tagCaptureRef = ref(null);
const tagTriggerRef = ref(null);
const tagChipLabel = useMultiSelectLabel(tagTriggerRef, localTags);

const filteredLabels = computed(() => {
  const q = tagSearch.value.toLowerCase();
  if (!q) return props.allLabels;
  return props.allLabels
    .filter(l => l.title.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.title.toLowerCase().indexOf(q) - b.title.toLowerCase().indexOf(q)
    );
});

function isTagActive(title) {
  return localTags.value.includes(title);
}

function toggleTag(title) {
  const idx = localTags.value.indexOf(title);
  if (idx === -1) localTags.value.push(title);
  else localTags.value.splice(idx, 1);
}

function removeTag(title, e) {
  e.stopPropagation();
  const idx = localTags.value.indexOf(title);
  if (idx !== -1) localTags.value.splice(idx, 1);
}

function tagColor(label) {
  const found = props.allLabels.find(
    l => l.title?.toLowerCase() === label.toLowerCase()
  );
  if (found?.color) return { bg: `${found.color}22`, text: found.color };
  return { bg: 'rgba(99,102,241,0.12)', text: '#4338ca' };
}

function openTagMenu() {
  closeMenus();
  showTagMenu.value = true;
  tagSearch.value = '';
  tagHighlight.value = 0;
  nextTick(() => tagCaptureRef.value?.focus());
}

function onTagInput(e) {
  tagSearch.value = e.target.value;
  tagHighlight.value = 0;
}

function onTagKeydown(e) {
  if (!showTagMenu.value) return;
  e.stopImmediatePropagation();
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    tagHighlight.value = Math.min(
      tagHighlight.value + 1,
      filteredLabels.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    tagHighlight.value = Math.max(tagHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const lbl = filteredLabels.value[tagHighlight.value];
    if (lbl) toggleTag(lbl.title);
  } else if (e.key === 'Escape') {
    showTagMenu.value = false;
    tagSearch.value = '';
  }
}

watch(showTagMenu, val => {
  if (val) window.addEventListener('keydown', onTagKeydown);
  else {
    window.removeEventListener('keydown', onTagKeydown);
    if (tagCaptureRef.value) tagCaptureRef.value.value = '';
  }
});
watch(filteredLabels, () => {
  tagHighlight.value = 0;
});
onUnmounted(() => window.removeEventListener('keydown', onTagKeydown));

// ── fonte ──────────────────────────────────────────────────────────────────
const { getLabels } = useNativeFieldOptions();
const SOURCE_OPTIONS = getLabels('source');
const localSources = ref([...props.selectedSources]);
const sourceSearch = ref('');
const sourceHighlight = ref(0);
const sourceCaptureRef = ref(null);
const sourceTriggerRef = ref(null);
const sourceChipLabel = useMultiSelectLabel(sourceTriggerRef, localSources);

const filteredSources = computed(() => {
  const q = sourceSearch.value.toLowerCase();
  if (!q) return SOURCE_OPTIONS;
  return SOURCE_OPTIONS.filter(s => s.toLowerCase().includes(q)).sort(
    (a, b) => a.toLowerCase().indexOf(q) - b.toLowerCase().indexOf(q)
  );
});

function isSourceActive(s) {
  return localSources.value.includes(s);
}

function toggleSource(s) {
  const idx = localSources.value.indexOf(s);
  if (idx === -1) localSources.value.push(s);
  else localSources.value.splice(idx, 1);
}

function openSourceMenu() {
  closeMenus();
  showSourceMenu.value = true;
  sourceSearch.value = '';
  sourceHighlight.value = 0;
  nextTick(() => sourceCaptureRef.value?.focus());
}

function onSourceInput(e) {
  sourceSearch.value = e.target.value;
  sourceHighlight.value = 0;
}

function onSourceKeydown(e) {
  if (!showSourceMenu.value) return;
  e.stopImmediatePropagation();
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    sourceHighlight.value = Math.min(
      sourceHighlight.value + 1,
      filteredSources.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    sourceHighlight.value = Math.max(sourceHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const s = filteredSources.value[sourceHighlight.value];
    if (s) toggleSource(s);
  } else if (e.key === 'Escape') {
    showSourceMenu.value = false;
    sourceSearch.value = '';
  }
}

watch(showSourceMenu, val => {
  if (val) window.addEventListener('keydown', onSourceKeydown);
  else {
    window.removeEventListener('keydown', onSourceKeydown);
    if (sourceCaptureRef.value) sourceCaptureRef.value.value = '';
  }
});
watch(filteredSources, () => {
  sourceHighlight.value = 0;
});
onUnmounted(() => window.removeEventListener('keydown', onSourceKeydown));

// ── produto ────────────────────────────────────────────────────────────────
const PRODUCT_OPTIONS = getLabels('product');
const localProducts = ref([...props.selectedProducts]);
const productSearch = ref('');
const productHighlight = ref(0);
const productCaptureRef = ref(null);
const productTriggerRef = ref(null);
const productChipLabel = useMultiSelectLabel(productTriggerRef, localProducts);

const filteredProducts = computed(() => {
  const q = productSearch.value.toLowerCase();
  if (!q) return PRODUCT_OPTIONS;
  return PRODUCT_OPTIONS.filter(p => p.toLowerCase().includes(q)).sort(
    (a, b) => a.toLowerCase().indexOf(q) - b.toLowerCase().indexOf(q)
  );
});

function isProductActive(p) {
  return localProducts.value.includes(p);
}

function toggleProduct(p) {
  const idx = localProducts.value.indexOf(p);
  if (idx === -1) localProducts.value.push(p);
  else localProducts.value.splice(idx, 1);
}

function openProductMenu() {
  closeMenus();
  showProductMenu.value = true;
  productSearch.value = '';
  productHighlight.value = 0;
  nextTick(() => productCaptureRef.value?.focus());
}

function onProductInput(e) {
  productSearch.value = e.target.value;
  productHighlight.value = 0;
}

function onProductKeydown(e) {
  if (!showProductMenu.value) return;
  e.stopImmediatePropagation();
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    productHighlight.value = Math.min(
      productHighlight.value + 1,
      filteredProducts.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    productHighlight.value = Math.max(productHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const p = filteredProducts.value[productHighlight.value];
    if (p) toggleProduct(p);
  } else if (e.key === 'Escape') {
    showProductMenu.value = false;
    productSearch.value = '';
  }
}

watch(showProductMenu, val => {
  if (val) window.addEventListener('keydown', onProductKeydown);
  else {
    window.removeEventListener('keydown', onProductKeydown);
    if (productCaptureRef.value) productCaptureRef.value.value = '';
  }
});
watch(filteredProducts, () => {
  productHighlight.value = 0;
});
onUnmounted(() => window.removeEventListener('keydown', onProductKeydown));

// ── cidade ─────────────────────────────────────────────────────────────────
const CITY_OPTIONS = getLabels('city');
const localCities = ref([...props.selectedCities]);
const citySearch = ref('');
const cityHighlight = ref(0);
const cityCaptureRef = ref(null);
const cityTriggerRef = ref(null);
const cityChipLabel = useMultiSelectLabel(cityTriggerRef, localCities);

const filteredCities = computed(() => {
  const q = citySearch.value.toLowerCase();
  if (!q) return CITY_OPTIONS;
  return CITY_OPTIONS.filter(c => c.toLowerCase().includes(q)).sort(
    (a, b) => a.toLowerCase().indexOf(q) - b.toLowerCase().indexOf(q)
  );
});

function isCityActive(c) {
  return localCities.value.includes(c);
}

function toggleCity(c) {
  const idx = localCities.value.indexOf(c);
  if (idx === -1) localCities.value.push(c);
  else localCities.value.splice(idx, 1);
}

function openCityMenu() {
  closeMenus();
  showCityMenu.value = true;
  citySearch.value = '';
  cityHighlight.value = 0;
  nextTick(() => cityCaptureRef.value?.focus());
}

function onCityInput(e) {
  citySearch.value = e.target.value;
  cityHighlight.value = 0;
}

function onCityKeydown(e) {
  if (!showCityMenu.value) return;
  e.stopImmediatePropagation();
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    cityHighlight.value = Math.min(
      cityHighlight.value + 1,
      filteredCities.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    cityHighlight.value = Math.max(cityHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const c = filteredCities.value[cityHighlight.value];
    if (c) toggleCity(c);
  } else if (e.key === 'Escape') {
    showCityMenu.value = false;
    citySearch.value = '';
  }
}

watch(showCityMenu, val => {
  if (val) window.addEventListener('keydown', onCityKeydown);
  else {
    window.removeEventListener('keydown', onCityKeydown);
    if (cityCaptureRef.value) cityCaptureRef.value.value = '';
  }
});
watch(filteredCities, () => {
  cityHighlight.value = 0;
});
onUnmounted(() => window.removeEventListener('keydown', onCityKeydown));

// ── previsão (closing date) ─────────────────────────────────────────────────
const closingDateFrom = ref(props.initialClosingDateFrom);
const closingDateTo = ref(props.initialClosingDateTo);

function setClosingDateRange({ from, to }) {
  closingDateFrom.value = from;
  closingDateTo.value = to;
}

const closingDateActiveCount = computed(
  () => (closingDateFrom.value ? 1 : 0) + (closingDateTo.value ? 1 : 0)
);

// ── valor ──────────────────────────────────────────────────────────────────
const valueMin = ref(props.initialValueMin);
const valueMax = ref(props.initialValueMax);
const valueActiveCount = computed(
  () => (valueMin.value ? 1 : 0) + (valueMax.value ? 1 : 0)
);

// ── seguidores ─────────────────────────────────────────────────────────────
const allAgents = computed(() => store.getters['agents/getAgents'] ?? []);
const localWatcherIds = ref([...props.selectedWatcherIds]);
const watcherSearch = ref('');
const watcherHighlight = ref(0);
const watcherCaptureRef = ref(null);
const watcherTriggerRef = ref(null);

const filteredAgents = computed(() => {
  const q = watcherSearch.value.toLowerCase();
  if (!q) return allAgents.value;
  return allAgents.value
    .filter(a => a.name.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.name.toLowerCase().indexOf(q) - b.name.toLowerCase().indexOf(q)
    );
});

function isWatcherActive(id) {
  return localWatcherIds.value.includes(id);
}

function toggleWatcher(id) {
  const idx = localWatcherIds.value.indexOf(id);
  if (idx === -1) localWatcherIds.value.push(id);
  else localWatcherIds.value.splice(idx, 1);
}

function removeWatcher(id, e) {
  e.stopPropagation();
  const idx = localWatcherIds.value.indexOf(id);
  if (idx !== -1) localWatcherIds.value.splice(idx, 1);
}

function watcherName(id) {
  return allAgents.value.find(a => a.id === id)?.name ?? '…';
}

const watcherLabels = computed(() =>
  localWatcherIds.value.map(id => watcherName(id))
);
const watcherChipLabel = useMultiSelectLabel(watcherTriggerRef, watcherLabels);

function openWatcherMenu() {
  closeMenus();
  showWatcherMenu.value = true;
  watcherSearch.value = '';
  watcherHighlight.value = 0;
  nextTick(() => watcherCaptureRef.value?.focus());
}

function onWatcherInput(e) {
  watcherSearch.value = e.target.value;
  watcherHighlight.value = 0;
}

function onWatcherKeydown(e) {
  if (!showWatcherMenu.value) return;
  e.stopImmediatePropagation();
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    watcherHighlight.value = Math.min(
      watcherHighlight.value + 1,
      filteredAgents.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    watcherHighlight.value = Math.max(watcherHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const a = filteredAgents.value[watcherHighlight.value];
    if (a) toggleWatcher(a.id);
  } else if (e.key === 'Escape') {
    showWatcherMenu.value = false;
    watcherSearch.value = '';
  }
}

watch(showWatcherMenu, val => {
  if (val) window.addEventListener('keydown', onWatcherKeydown);
  else {
    window.removeEventListener('keydown', onWatcherKeydown);
    if (watcherCaptureRef.value) watcherCaptureRef.value.value = '';
  }
});
watch(filteredAgents, () => {
  watcherHighlight.value = 0;
});
onUnmounted(() => window.removeEventListener('keydown', onWatcherKeydown));

// ── data ───────────────────────────────────────────────────────────────────
const dateFrom = ref(props.initialDateFrom);
const dateTo = ref(props.initialDateTo);
const dateField = ref(
  props.initialDateField === 'created_at' ? 'created_at' : 'updated_at'
);

const DATE_FIELD_OPTIONS = [
  { value: 'created_at', labelKey: 'KANBAN.FILTERS.DATE_FIELD_CREATED' },
  { value: 'updated_at', labelKey: 'KANBAN.FILTERS.DATE_FIELD_UPDATED' },
];

function setDateRange({ from, to }) {
  dateFrom.value = from;
  dateTo.value = to;
}

const dateActiveCount = computed(
  () => (dateFrom.value ? 1 : 0) + (dateTo.value ? 1 : 0)
);

// ── atributos personalizados ───────────────────────────────────────────────
// only fields we support filtering on
const SUPPORTED_TYPES = ['list', 'text', 'checkbox', 'number', 'date'];
const GROUPS = [
  { key: 'opportunity', attributes: opportunityAttributes },
  { key: 'contact', attributes: contactAttributes },
  { key: 'conversation', attributes: conversationAttributes },
];

const filterableGroups = computed(() =>
  GROUPS.map(g => ({
    ...g,
    attributes: g.attributes.value.filter(a =>
      SUPPORTED_TYPES.includes(a.attributeDisplayType)
    ),
  })).filter(g => g.attributes.length > 0)
);

const hasAnyFilterableAttribute = computed(() =>
  filterableGroups.value.some(g => g.attributes.length > 0)
);

// ── pastas de filtro (Oportunidade agrupada por kanbanFolders + Contato/Conversa fixas) ────
const filterableOpportunityAttributes = computed(() =>
  opportunityAttributes.value.filter(a =>
    SUPPORTED_TYPES.includes(a.attributeDisplayType)
  )
);

const filterFolders = computed(() => {
  const opportunityFolderGroups = opportunityFolders.value
    .map(folder => ({
      id: `folder:${folder.id}`,
      groupKey: 'opportunity',
      name: folder.name,
      attributes: filterableOpportunityAttributes.value.filter(
        a => a.folderId === folder.id
      ),
    }))
    .filter(f => f.attributes.length > 0);

  const contactFolder = {
    id: 'folder:contact',
    groupKey: 'contact',
    name: t('KANBAN.ADVANCED_FILTERS.CONTACT_FOLDER'),
    attributes: contactAttributes.value.filter(a =>
      SUPPORTED_TYPES.includes(a.attributeDisplayType)
    ),
  };

  const conversationFolder = {
    id: 'folder:conversation',
    groupKey: 'conversation',
    name: t('KANBAN.ADVANCED_FILTERS.CONVERSATION_FOLDER'),
    attributes: conversationAttributes.value.filter(a =>
      SUPPORTED_TYPES.includes(a.attributeDisplayType)
    ),
  };

  return [
    ...opportunityFolderGroups,
    ...(contactFolder.attributes.length ? [contactFolder] : []),
    ...(conversationFolder.attributes.length ? [conversationFolder] : []),
  ];
});

// which field accordion is open — identified by "groupKey:attributeKey"
const openField = ref(null);

function fieldId(groupKey, attributeKey) {
  return `${groupKey}:${attributeKey}`;
}

function toggleField(groupKey, attributeKey) {
  const id = fieldId(groupKey, attributeKey);
  openField.value = openField.value === id ? null : id;
}

// which filter folder accordion is open — only one at a time
const openFolder = ref(null);

function toggleFolder(folderId) {
  openFolder.value = openFolder.value === folderId ? null : folderId;
  openField.value = null;
}

// filter values per group per field: { opportunity: { [attributeKey]: value }, contact: {...}, conversation: {...} }
// list   → array of strings
// text   → string
// checkbox → 'true' | 'false' | null
// number → { min: '', max: '' }
const values = ref({ opportunity: {}, contact: {}, conversation: {} });

function getListValue(groupKey, key) {
  return values.value[groupKey][key] ?? [];
}

function getBoolValue(groupKey, key) {
  return values.value[groupKey][key] ?? null;
}

const TEXT_OPERATOR_KEYS = [
  'eq',
  'not_eq',
  'contains',
  'not_contains',
  'present',
  'blank',
];
const NUMBER_OPERATOR_KEYS = [
  'eq',
  'not_eq',
  'gt',
  'gte',
  'lt',
  'lte',
  'present',
  'blank',
];
const DATE_OPERATOR_KEYS = [
  'today',
  'tomorrow',
  'yesterday',
  'on',
  'after',
  'before',
  'within_days',
  'before_days',
];

const NO_VALUE_OPERATORS = [
  'present',
  'blank',
  'today',
  'tomorrow',
  'yesterday',
];
const RELATIVE_RANGE_OPERATORS = ['within_days', 'before_days'];
const SINGLE_DATE_OPERATORS = ['on', 'after', 'before'];

function operatorKeysFor(attributeDisplayType) {
  if (attributeDisplayType === 'number') return NUMBER_OPERATOR_KEYS;
  if (attributeDisplayType === 'date') return DATE_OPERATOR_KEYS;
  return TEXT_OPERATOR_KEYS;
}

function operatorOptionsFor(attributeDisplayType) {
  return operatorKeysFor(attributeDisplayType).map(key => ({
    value: key,
    label: t(`FLOWS.FILTER.OPERATORS.${key.toUpperCase()}`),
  }));
}

function getOperatorValue(groupKey, key) {
  const current = values.value[groupKey][key];
  return current && typeof current === 'object' && 'operator' in current
    ? current
    : { operator: '', value: [] };
}

function setOperator(groupKey, key, operator) {
  values.value[groupKey] = {
    ...values.value[groupKey],
    [key]: { operator, value: [] },
  };
}

function setOperatorValue(groupKey, key, value) {
  const current = getOperatorValue(groupKey, key);
  values.value[groupKey] = {
    ...values.value[groupKey],
    [key]: { ...current, value },
  };
}

function attributeHasValue(groupKey, attr) {
  const key = attr.attributeKey;
  if (attr.attributeDisplayType === 'list') {
    return getListValue(groupKey, key).length > 0;
  }
  if (attr.attributeDisplayType === 'checkbox') {
    return getBoolValue(groupKey, key) !== null;
  }
  if (['text', 'number', 'date'].includes(attr.attributeDisplayType)) {
    const { operator, value } = getOperatorValue(groupKey, key);
    if (!operator) return false;
    if (NO_VALUE_OPERATORS.includes(operator)) return true;
    if (value.length === 0) return false;
    if (typeof value[0] === 'object') return Boolean(value[0].count);
    return Boolean(value[0]);
  }
  return false;
}

function folderActiveCount(folder) {
  return folder.attributes.filter(attr =>
    attributeHasValue(folder.groupKey, attr)
  ).length;
}

function toggleListOption(groupKey, key, option) {
  const current = getListValue(groupKey, key);
  const idx = current.indexOf(option);
  const next =
    idx === -1 ? [...current, option] : current.filter(v => v !== option);
  values.value[groupKey] = { ...values.value[groupKey], [key]: next };
}

function setBool(groupKey, key, val) {
  const next = values.value[groupKey][key] === val ? null : val;
  values.value[groupKey] = { ...values.value[groupKey], [key]: next };
}

function clearAll() {
  values.value = { opportunity: {}, contact: {}, conversation: {} };
  localTags.value = [];
  tagSearch.value = '';
  localSources.value = [];
  sourceSearch.value = '';
  localProducts.value = [];
  productSearch.value = '';
  localCities.value = [];
  citySearch.value = '';
  closingDateFrom.value = '';
  closingDateTo.value = '';
  valueMin.value = '';
  valueMax.value = '';
  localWatcherIds.value = [];
  watcherSearch.value = '';
  dateFrom.value = '';
  dateTo.value = '';
  dateField.value = 'created_at';
}

function applyFilters() {
  const payload = {};
  GROUPS.forEach(({ key: groupKey }) => {
    if (Object.keys(values.value[groupKey]).length) {
      payload[groupKey] = { ...values.value[groupKey] };
    }
  });
  if (localTags.value.length) payload.labels = [...localTags.value];
  if (localSources.value.length) payload.opp_source = [...localSources.value];
  if (localProducts.value.length) {
    payload.opp_products = [...localProducts.value];
  }
  if (localCities.value.length) payload.city = [...localCities.value];
  if (closingDateFrom.value) payload.closing_date_from = closingDateFrom.value;
  if (closingDateTo.value) payload.closing_date_to = closingDateTo.value;
  if (valueMin.value) payload.value_min = valueMin.value;
  if (valueMax.value) payload.value_max = valueMax.value;
  if (localWatcherIds.value.length) {
    payload.watcher_ids = [...localWatcherIds.value];
  }
  if (dateFrom.value) payload.date_from = dateFrom.value;
  if (dateTo.value) payload.date_to = dateTo.value;
  if ((dateFrom.value || dateTo.value) && dateField.value !== 'created_at') {
    payload.date_field = dateField.value;
  }
  emit('filterChange', payload);
  emit('close');
}
</script>

<template>
  <div
    class="w-72 max-h-[min(32rem,calc(100vh-96px))] bg-n-background border border-n-weak rounded-xl shadow-2xl overflow-y-auto flex flex-col"
    @click="closeMenus"
  >
    <!-- data -->
    <div class="border-b border-n-weak">
      <button
        class="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-n-alpha-1"
        @click="openField = openField === '__date__' ? null : '__date__'"
      >
        <span class="flex items-center gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('KANBAN.FILTERS.DATE_LABEL') }}
          </span>
          <span
            v-if="dateActiveCount"
            class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wide bg-n-brand text-white"
          >
            {{ dateActiveCount }}
          </span>
        </span>
        <span
          class="text-[11px] text-n-slate-10 transition-transform duration-150"
          :class="{ 'rotate-180': openField === '__date__' }"
          aria-hidden="true"
        >
          ▼
        </span>
      </button>
      <div v-if="openField === '__date__'" class="px-4 pb-3">
        <div class="flex flex-col gap-1 pb-2 border-b border-n-weak mb-2">
          <div
            class="flex w-full rounded-md border border-n-weak overflow-hidden"
            @click.stop
          >
            <button
              v-for="opt in DATE_FIELD_OPTIONS"
              :key="opt.value"
              class="flex-1 px-2 py-1.5 text-xs font-medium text-center whitespace-nowrap transition-colors"
              :class="
                dateField === opt.value
                  ? 'bg-n-brand text-white'
                  : 'bg-n-surface-2 text-n-slate-11 hover:bg-n-alpha-1'
              "
              @click="dateField = opt.value"
            >
              {{ $t(opt.labelKey) }}
            </button>
          </div>
        </div>
        <DateRangePicker
          :from="dateFrom"
          :to="dateTo"
          @update:range="setDateRange"
        />
      </div>
    </div>

    <!-- fonte -->
    <div class="border-b border-n-weak px-4 py-2.5">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('KANBAN.FILTERS.SOURCE') }}
      </span>
      <div class="mt-1.5" @click.stop>
        <input
          ref="sourceCaptureRef"
          type="text"
          class="sr-only"
          tabindex="-1"
          @input="onSourceInput"
        />
        <button
          ref="sourceTriggerRef"
          class="w-full flex items-center justify-between gap-2 border rounded-lg px-2.5 py-1.5 text-sm transition-colors focus:outline-none"
          :class="
            showSourceMenu || localSources.length
              ? 'border-n-brand text-n-brand bg-n-alpha-2'
              : 'border-n-weak text-n-slate-12 bg-n-background'
          "
          @click="showSourceMenu ? (showSourceMenu = false) : openSourceMenu()"
        >
          <span class="truncate">
            <template v-if="showSourceMenu && sourceSearch">{{
              sourceSearch
            }}</template>
            <template v-else-if="localSources.length">{{
              sourceChipLabel
            }}</template>
            <template v-else>{{
              $t('KANBAN.FILTERS.SOURCE_PLACEHOLDER')
            }}</template>
          </span>
          <span class="i-lucide-chevron-down size-3.5 flex-shrink-0" />
        </button>

        <div
          v-if="showSourceMenu"
          class="mt-1 bg-n-background border border-n-weak rounded-xl shadow-xl max-h-52 overflow-y-auto py-1"
        >
          <button
            v-if="!sourceSearch"
            class="w-full flex items-center px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              localSources.length === 0
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12'
            "
            @click="
              localSources = [];
              showSourceMenu = false;
            "
          >
            {{ $t('KANBAN.FILTERS.ALL') }}
          </button>
          <button
            v-for="(s, idx) in filteredSources"
            :key="s"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              idx === sourceHighlight
                ? 'bg-n-alpha-1 text-n-slate-12 font-medium'
                : isSourceActive(s)
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="toggleSource(s)"
            @mouseenter="sourceHighlight = idx"
          >
            <span class="truncate">{{ s }}</span>
            <span
              v-if="isSourceActive(s)"
              class="i-lucide-check size-3 ml-auto flex-shrink-0 text-n-brand"
            />
          </button>
          <p
            v-if="filteredSources.length === 0"
            class="px-3 py-2 text-sm text-n-slate-10 italic text-center"
          >
            {{ $t('KANBAN.SETTINGS.NO_RESULTS') }}
          </p>
        </div>
      </div>
    </div>

    <!-- produto -->
    <div class="border-b border-n-weak px-4 py-2.5">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('KANBAN.FILTERS.PRODUCT') }}
      </span>
      <div class="mt-1.5" @click.stop>
        <input
          ref="productCaptureRef"
          type="text"
          class="sr-only"
          tabindex="-1"
          @input="onProductInput"
        />
        <button
          ref="productTriggerRef"
          class="w-full flex items-center justify-between gap-2 border rounded-lg px-2.5 py-1.5 text-sm transition-colors focus:outline-none"
          :class="
            showProductMenu || localProducts.length
              ? 'border-n-brand text-n-brand bg-n-alpha-2'
              : 'border-n-weak text-n-slate-12 bg-n-background'
          "
          @click="
            showProductMenu ? (showProductMenu = false) : openProductMenu()
          "
        >
          <span class="truncate">
            <template v-if="showProductMenu && productSearch">{{
              productSearch
            }}</template>
            <template v-else-if="localProducts.length">{{
              productChipLabel
            }}</template>
            <template v-else>{{
              $t('KANBAN.FILTERS.PRODUCT_PLACEHOLDER')
            }}</template>
          </span>
          <span class="i-lucide-chevron-down size-3.5 flex-shrink-0" />
        </button>

        <div
          v-if="showProductMenu"
          class="mt-1 bg-n-background border border-n-weak rounded-xl shadow-xl max-h-52 overflow-y-auto py-1"
        >
          <button
            v-if="!productSearch"
            class="w-full flex items-center px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              localProducts.length === 0
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12'
            "
            @click="
              localProducts = [];
              showProductMenu = false;
            "
          >
            {{ $t('KANBAN.FILTERS.ALL') }}
          </button>
          <button
            v-for="(p, idx) in filteredProducts"
            :key="p"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              idx === productHighlight
                ? 'bg-n-alpha-1 text-n-slate-12 font-medium'
                : isProductActive(p)
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="toggleProduct(p)"
            @mouseenter="productHighlight = idx"
          >
            <span class="truncate">{{ p }}</span>
            <span
              v-if="isProductActive(p)"
              class="i-lucide-check size-3 ml-auto flex-shrink-0 text-n-brand"
            />
          </button>
          <p
            v-if="filteredProducts.length === 0"
            class="px-3 py-2 text-sm text-n-slate-10 italic text-center"
          >
            {{ $t('KANBAN.SETTINGS.NO_RESULTS') }}
          </p>
        </div>
      </div>
    </div>

    <!-- cidade -->
    <div class="border-b border-n-weak px-4 py-2.5">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('KANBAN.FILTERS.CITY') }}
      </span>
      <div class="mt-1.5" @click.stop>
        <input
          ref="cityCaptureRef"
          type="text"
          class="sr-only"
          tabindex="-1"
          @input="onCityInput"
        />
        <button
          ref="cityTriggerRef"
          class="w-full flex items-center justify-between gap-2 border rounded-lg px-2.5 py-1.5 text-sm transition-colors focus:outline-none"
          :class="
            showCityMenu || localCities.length
              ? 'border-n-brand text-n-brand bg-n-alpha-2'
              : 'border-n-weak text-n-slate-12 bg-n-background'
          "
          @click="showCityMenu ? (showCityMenu = false) : openCityMenu()"
        >
          <span class="truncate">
            <template v-if="showCityMenu && citySearch">{{
              citySearch
            }}</template>
            <template v-else-if="localCities.length">{{
              cityChipLabel
            }}</template>
            <template v-else>{{
              $t('KANBAN.FILTERS.CITY_PLACEHOLDER')
            }}</template>
          </span>
          <span class="i-lucide-chevron-down size-3.5 flex-shrink-0" />
        </button>

        <div
          v-if="showCityMenu"
          class="mt-1 bg-n-background border border-n-weak rounded-xl shadow-xl max-h-52 overflow-y-auto py-1"
        >
          <button
            v-if="!citySearch"
            class="w-full flex items-center px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              localCities.length === 0
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12'
            "
            @click="
              localCities = [];
              showCityMenu = false;
            "
          >
            {{ $t('KANBAN.FILTERS.ALL') }}
          </button>
          <button
            v-for="(c, idx) in filteredCities"
            :key="c"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              idx === cityHighlight
                ? 'bg-n-alpha-1 text-n-slate-12 font-medium'
                : isCityActive(c)
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="toggleCity(c)"
            @mouseenter="cityHighlight = idx"
          >
            <span class="truncate">{{ c }}</span>
            <span
              v-if="isCityActive(c)"
              class="i-lucide-check size-3 ml-auto flex-shrink-0 text-n-brand"
            />
          </button>
          <p
            v-if="filteredCities.length === 0"
            class="px-3 py-2 text-sm text-n-slate-10 italic text-center"
          >
            {{ $t('KANBAN.SETTINGS.NO_RESULTS') }}
          </p>
        </div>
      </div>
    </div>

    <!-- previsão -->
    <div class="border-b border-n-weak">
      <button
        class="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-n-alpha-1"
        @click="
          openField =
            openField === '__closing_date__' ? null : '__closing_date__'
        "
      >
        <span class="flex items-center gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('KANBAN.FILTERS.CLOSING_DATE') }}
          </span>
          <span
            v-if="closingDateActiveCount"
            class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wide bg-n-brand text-white"
          >
            {{ closingDateActiveCount }}
          </span>
        </span>
        <span
          class="text-[11px] text-n-slate-10 transition-transform duration-150"
          :class="{ 'rotate-180': openField === '__closing_date__' }"
          aria-hidden="true"
        >
          ▼
        </span>
      </button>
      <div v-if="openField === '__closing_date__'" class="px-4 pb-3">
        <DateRangePicker
          :from="closingDateFrom"
          :to="closingDateTo"
          @update:range="setClosingDateRange"
        />
      </div>
    </div>

    <!-- valor -->
    <div class="border-b border-n-weak">
      <button
        class="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-n-alpha-1"
        @click="openField = openField === '__value__' ? null : '__value__'"
      >
        <span class="flex items-center gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('KANBAN.FILTERS.VALUE') }}
          </span>
          <span
            v-if="valueActiveCount"
            class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wide bg-n-brand text-white"
          >
            {{ valueActiveCount }}
          </span>
        </span>
        <span
          class="text-[11px] text-n-slate-10 transition-transform duration-150"
          :class="{ 'rotate-180': openField === '__value__' }"
          aria-hidden="true"
        >
          ▼
        </span>
      </button>
      <div
        v-if="openField === '__value__'"
        class="px-4 pb-3 flex items-center gap-2"
      >
        <div
          class="flex-1 flex items-center !h-8 bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 transition-colors"
        >
          <input
            v-model="valueMin"
            type="number"
            class="reset-base w-full !h-auto text-sm text-n-slate-12 !bg-transparent !outline-none !shadow-none !border-none !p-0 ring-0 focus:ring-0 appearance-none placeholder:text-n-slate-9"
            :placeholder="$t('KANBAN.ADVANCED_FILTERS.NUMBER_MIN')"
            @click.stop
          />
        </div>
        <span class="text-xs text-n-slate-10">{{
          $t('KANBAN.ADVANCED_FILTERS.NUMBER_TO')
        }}</span>
        <div
          class="flex-1 flex items-center !h-8 bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 transition-colors"
        >
          <input
            v-model="valueMax"
            type="number"
            class="reset-base w-full !h-auto text-sm text-n-slate-12 !bg-transparent !outline-none !shadow-none !border-none !p-0 ring-0 focus:ring-0 appearance-none placeholder:text-n-slate-9"
            :placeholder="$t('KANBAN.ADVANCED_FILTERS.NUMBER_MAX')"
            @click.stop
          />
        </div>
      </div>
    </div>

    <!-- seguidores -->
    <div class="border-b border-n-weak px-4 py-2.5">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('KANBAN.FILTERS.WATCHERS') }}
      </span>
      <div class="mt-1.5" @click.stop>
        <input
          ref="watcherCaptureRef"
          type="text"
          class="sr-only"
          tabindex="-1"
          @input="onWatcherInput"
        />
        <button
          ref="watcherTriggerRef"
          class="w-full flex items-center justify-between gap-2 border rounded-lg px-2.5 py-1.5 text-sm transition-colors focus:outline-none"
          :class="
            showWatcherMenu || localWatcherIds.length
              ? 'border-n-brand text-n-brand bg-n-alpha-2'
              : 'border-n-weak text-n-slate-12 bg-n-background'
          "
          @click="
            showWatcherMenu ? (showWatcherMenu = false) : openWatcherMenu()
          "
        >
          <span v-if="showWatcherMenu && watcherSearch" class="truncate">{{
            watcherSearch
          }}</span>
          <span v-else-if="localWatcherIds.length" class="truncate">{{
            watcherChipLabel
          }}</span>
          <span v-else class="truncate">{{
            $t('KANBAN.FILTERS.WATCHERS_PLACEHOLDER')
          }}</span>
          <span class="i-lucide-chevron-down size-3.5 flex-shrink-0" />
        </button>

        <div
          v-if="showWatcherMenu"
          class="mt-1 bg-n-background border border-n-weak rounded-xl shadow-xl max-h-52 overflow-y-auto py-1"
        >
          <button
            v-for="(a, idx) in filteredAgents"
            :key="a.id"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              idx === watcherHighlight
                ? 'bg-n-alpha-1 text-n-slate-12 font-medium'
                : isWatcherActive(a.id)
                  ? 'text-n-slate-12 font-medium'
                  : 'text-n-slate-11'
            "
            @click="toggleWatcher(a.id)"
            @mouseenter="watcherHighlight = idx"
          >
            <span class="truncate">{{ a.name }}</span>
            <span
              v-if="isWatcherActive(a.id)"
              class="i-lucide-check size-3 ml-auto flex-shrink-0 text-n-brand"
            />
          </button>
          <p
            v-if="filteredAgents.length === 0"
            class="px-3 py-2 text-sm text-n-slate-10 italic text-center"
          >
            {{ $t('KANBAN.SETTINGS.NO_RESULTS') }}
          </p>
        </div>
      </div>
      <div
        v-if="localWatcherIds.length"
        class="flex items-center gap-1 flex-wrap mt-1.5"
      >
        <span
          v-for="id in localWatcherIds"
          :key="id"
          class="group/watcher inline-flex items-center h-5 text-[11px] leading-none font-medium px-1.5 rounded shrink-0 bg-n-alpha-2 text-n-slate-12"
        >
          {{ watcherName(id) }}
          <span
            class="overflow-hidden transition-all duration-150 max-w-0 group-hover/watcher:max-w-[12px] flex items-center justify-center"
          >
            <button
              class="flex items-center justify-center hover:bg-black/10 rounded-full ml-[2px]"
              @click="removeWatcher(id, $event)"
            >
              <span class="i-lucide-x size-[8px] flex-shrink-0" />
            </button>
          </span>
        </span>
      </div>
    </div>

    <!-- etiquetas -->
    <div class="border-b border-n-weak px-4 py-2.5">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('KANBAN.FILTERS.TAGS') }}
      </span>
      <div class="mt-1.5" @click.stop>
        <input
          ref="tagCaptureRef"
          type="text"
          class="sr-only"
          tabindex="-1"
          @input="onTagInput"
        />
        <button
          ref="tagTriggerRef"
          class="w-full flex items-center justify-between gap-2 border rounded-lg px-2.5 py-1.5 text-sm transition-colors focus:outline-none"
          :class="
            showTagMenu || localTags.length
              ? 'border-n-brand text-n-brand bg-n-alpha-2'
              : 'border-n-weak text-n-slate-12 bg-n-background'
          "
          @click="showTagMenu ? (showTagMenu = false) : openTagMenu()"
        >
          <span v-if="showTagMenu && tagSearch" class="truncate">{{
            tagSearch
          }}</span>
          <span v-else-if="localTags.length" class="truncate">{{
            tagChipLabel
          }}</span>
          <span v-else class="truncate">{{
            $t('KANBAN.FILTERS.TAGS_PLACEHOLDER')
          }}</span>
          <span class="i-lucide-chevron-down size-3.5 flex-shrink-0" />
        </button>

        <div
          v-if="showTagMenu"
          class="mt-1 bg-n-background border border-n-weak rounded-xl shadow-xl max-h-52 overflow-y-auto py-1"
        >
          <button
            v-for="(lbl, idx) in filteredLabels"
            :key="lbl.id"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              idx === tagHighlight
                ? 'bg-n-alpha-1 text-n-slate-12 font-medium'
                : isTagActive(lbl.title)
                  ? 'text-n-slate-12 font-medium'
                  : 'text-n-slate-11'
            "
            @click="toggleTag(lbl.title)"
            @mouseenter="tagHighlight = idx"
          >
            <span
              class="size-2.5 rounded-full flex-shrink-0"
              :style="{ backgroundColor: lbl.color }"
            />
            <span class="truncate">{{ lbl.title }}</span>
            <span
              v-if="isTagActive(lbl.title)"
              class="i-lucide-check size-3 ml-auto flex-shrink-0 text-n-brand"
            />
          </button>
          <p
            v-if="filteredLabels.length === 0"
            class="px-3 py-2 text-sm text-n-slate-10 italic text-center"
          >
            {{ $t('KANBAN.SETTINGS.NO_RESULTS') }}
          </p>
        </div>
      </div>
      <div
        v-if="localTags.length"
        class="flex items-center gap-1 flex-wrap mt-1.5"
      >
        <span
          v-for="tag in localTags"
          :key="tag"
          class="group/tag inline-flex items-center h-5 text-[11px] leading-none font-medium px-1.5 rounded shrink-0"
          :style="{
            backgroundColor: tagColor(tag).bg,
            color: tagColor(tag).text,
          }"
        >
          {{ tag }}
          <span
            class="overflow-hidden transition-all duration-150 max-w-0 group-hover/tag:max-w-[12px] flex items-center justify-center"
          >
            <button
              class="flex items-center justify-center hover:bg-black/10 rounded-full ml-[2px]"
              @click="removeTag(tag, $event)"
            >
              <span class="i-lucide-x size-[8px] flex-shrink-0" />
            </button>
          </span>
        </span>
      </div>
    </div>

    <!-- field list -->
    <template>
      <div
        v-if="!hasAnyFilterableAttribute"
        class="text-sm text-n-slate-10 text-center py-8"
      >
        {{ $t('KANBAN.ADVANCED_FILTERS.EMPTY') }}
      </div>

      <div
        v-for="folder in filterFolders"
        :key="folder.id"
        class="border-b border-n-weak"
      >
        <!-- folder accordion header -->
        <button
          class="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-n-alpha-1"
          @click="toggleFolder(folder.id)"
        >
          <span class="flex items-center gap-2">
            <span class="i-lucide-folder size-3.5 text-n-slate-10" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ folder.name }}
            </span>
            <span
              v-if="folderActiveCount(folder)"
              class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wide bg-n-brand text-white"
            >
              {{ folderActiveCount(folder) }}
            </span>
          </span>
          <span
            class="text-[11px] text-n-slate-10 transition-transform duration-150"
            :class="{ 'rotate-180': openFolder === folder.id }"
            aria-hidden="true"
          >
            ▼
          </span>
        </button>

        <!-- folder body: nested field accordions -->
        <div v-if="openFolder === folder.id" class="pl-3 pb-1">
          <div
            v-for="attr in folder.attributes"
            :key="fieldId(folder.groupKey, attr.attributeKey)"
            class="border-l-[3px] transition-colors"
            :class="
              openField === fieldId(folder.groupKey, attr.attributeKey)
                ? 'border-l-n-blue-9 bg-n-surface-2'
                : 'border-l-transparent'
            "
          >
            <!-- accordion header -->
            <button
              class="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-n-alpha-1"
              @click="toggleField(folder.groupKey, attr.attributeKey)"
            >
              <span
                class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide"
              >
                {{ attr.attributeDisplayName }}
              </span>
              <span
                class="text-[11px] text-n-slate-10 transition-transform duration-150"
                :class="{
                  'rotate-180':
                    openField === fieldId(folder.groupKey, attr.attributeKey),
                }"
                aria-hidden="true"
              >
                ▼
              </span>
            </button>

            <!-- accordion body -->
            <div
              v-if="openField === fieldId(folder.groupKey, attr.attributeKey)"
              class="px-4 pb-3"
            >
              <!-- LIST type: checkboxes -->
              <div
                v-if="attr.attributeDisplayType === 'list'"
                class="flex flex-col gap-1"
              >
                <button
                  v-for="opt in attr.attributeValues"
                  :key="optKey(opt)"
                  class="flex items-center gap-2 px-2 py-1 rounded min-h-[30px] bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors text-left"
                  @click="
                    toggleListOption(
                      folder.groupKey,
                      attr.attributeKey,
                      optKey(opt)
                    )
                  "
                >
                  <span class="text-sm text-n-slate-12 flex-1 truncate">{{
                    optLabel(opt)
                  }}</span>
                  <span
                    v-if="
                      getListValue(folder.groupKey, attr.attributeKey).includes(
                        optKey(opt)
                      )
                    "
                    class="i-lucide-check size-3.5 text-n-brand flex-shrink-0"
                  />
                </button>
              </div>

              <!-- TEXT type: operator + value -->
              <div
                v-else-if="attr.attributeDisplayType === 'text'"
                class="flex flex-col gap-1.5"
              >
                <KanbanFilterOperatorSelect
                  :model-value="
                    getOperatorValue(folder.groupKey, attr.attributeKey)
                      .operator
                  "
                  :options="operatorOptionsFor('text')"
                  :placeholder="
                    $t('KANBAN.ADVANCED_FILTERS.OPERATOR_PLACEHOLDER')
                  "
                  @update:model-value="
                    setOperator(folder.groupKey, attr.attributeKey, $event)
                  "
                />
                <template
                  v-if="
                    NO_VALUE_OPERATORS.includes(
                      getOperatorValue(folder.groupKey, attr.attributeKey)
                        .operator
                    )
                  "
                />
                <div
                  v-else-if="
                    getOperatorValue(folder.groupKey, attr.attributeKey)
                      .operator
                  "
                  class="flex items-center !h-8 bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 transition-colors"
                >
                  <input
                    :value="
                      getOperatorValue(folder.groupKey, attr.attributeKey)
                        .value[0] ?? ''
                    "
                    type="text"
                    class="reset-base flex-1 !h-auto text-sm text-n-slate-12 !bg-transparent !outline-none !shadow-none !border-none !p-0 ring-0 focus:ring-0 w-full appearance-none placeholder:text-n-slate-9"
                    :placeholder="
                      $t('KANBAN.ADVANCED_FILTERS.TEXT_PLACEHOLDER')
                    "
                    @input="
                      setOperatorValue(folder.groupKey, attr.attributeKey, [
                        $event.target.value,
                      ])
                    "
                  />
                </div>
              </div>

              <!-- CHECKBOX / BOOLEAN type -->
              <div
                v-else-if="attr.attributeDisplayType === 'checkbox'"
                class="flex gap-2"
              >
                <button
                  class="flex-1 text-sm py-1 min-h-[30px] rounded font-medium transition-colors"
                  :class="
                    getBoolValue(folder.groupKey, attr.attributeKey) === 'true'
                      ? 'bg-n-brand text-white'
                      : 'bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12'
                  "
                  @click="setBool(folder.groupKey, attr.attributeKey, 'true')"
                >
                  {{ $t('KANBAN.ADVANCED_FILTERS.BOOL_YES') }}
                </button>
                <button
                  class="flex-1 text-sm py-1 min-h-[30px] rounded font-medium transition-colors"
                  :class="
                    getBoolValue(folder.groupKey, attr.attributeKey) === 'false'
                      ? 'bg-n-brand text-white'
                      : 'bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12'
                  "
                  @click="setBool(folder.groupKey, attr.attributeKey, 'false')"
                >
                  {{ $t('KANBAN.ADVANCED_FILTERS.BOOL_NO') }}
                </button>
              </div>

              <!-- NUMBER type: operator + value -->
              <div
                v-else-if="attr.attributeDisplayType === 'number'"
                class="flex flex-col gap-1.5"
              >
                <KanbanFilterOperatorSelect
                  :model-value="
                    getOperatorValue(folder.groupKey, attr.attributeKey)
                      .operator
                  "
                  :options="operatorOptionsFor('number')"
                  :placeholder="
                    $t('KANBAN.ADVANCED_FILTERS.OPERATOR_PLACEHOLDER')
                  "
                  @update:model-value="
                    setOperator(folder.groupKey, attr.attributeKey, $event)
                  "
                />
                <template
                  v-if="
                    NO_VALUE_OPERATORS.includes(
                      getOperatorValue(folder.groupKey, attr.attributeKey)
                        .operator
                    )
                  "
                />
                <div
                  v-else-if="
                    getOperatorValue(folder.groupKey, attr.attributeKey)
                      .operator
                  "
                  class="flex items-center !h-8 bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 transition-colors"
                >
                  <input
                    :value="
                      getOperatorValue(folder.groupKey, attr.attributeKey)
                        .value[0] ?? ''
                    "
                    type="number"
                    class="reset-base w-full !h-auto text-sm text-n-slate-12 !bg-transparent !outline-none !shadow-none !border-none !p-0 ring-0 focus:ring-0 appearance-none placeholder:text-n-slate-9"
                    @input="
                      setOperatorValue(folder.groupKey, attr.attributeKey, [
                        $event.target.value,
                      ])
                    "
                  />
                </div>
              </div>

              <!-- DATE type: operator + value -->
              <div
                v-else-if="attr.attributeDisplayType === 'date'"
                class="flex flex-col gap-1.5"
              >
                <KanbanFilterOperatorSelect
                  :model-value="
                    getOperatorValue(folder.groupKey, attr.attributeKey)
                      .operator
                  "
                  :options="operatorOptionsFor('date')"
                  :placeholder="
                    $t('KANBAN.ADVANCED_FILTERS.OPERATOR_PLACEHOLDER')
                  "
                  @update:model-value="
                    setOperator(folder.groupKey, attr.attributeKey, $event)
                  "
                />
                <template
                  v-if="
                    NO_VALUE_OPERATORS.includes(
                      getOperatorValue(folder.groupKey, attr.attributeKey)
                        .operator
                    )
                  "
                />
                <input
                  v-else-if="
                    SINGLE_DATE_OPERATORS.includes(
                      getOperatorValue(folder.groupKey, attr.attributeKey)
                        .operator
                    )
                  "
                  type="date"
                  :value="
                    getOperatorValue(folder.groupKey, attr.attributeKey)
                      .value[0] ?? ''
                  "
                  class="!h-8 border border-n-weak rounded-lg px-3 text-sm bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
                  @change="
                    setOperatorValue(
                      folder.groupKey,
                      attr.attributeKey,
                      $event.target.value ? [$event.target.value] : []
                    )
                  "
                />
                <KanbanFilterDateRangeInput
                  v-else-if="
                    RELATIVE_RANGE_OPERATORS.includes(
                      getOperatorValue(folder.groupKey, attr.attributeKey)
                        .operator
                    )
                  "
                  :model-value="
                    getOperatorValue(folder.groupKey, attr.attributeKey).value
                  "
                  @update:model-value="
                    setOperatorValue(folder.groupKey, attr.attributeKey, $event)
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- footer -->
    <div
      class="sticky bottom-0 flex items-center justify-between px-4 py-2.5 border-t border-n-weak bg-n-background"
    >
      <button
        class="text-sm text-n-slate-10 hover:text-n-slate-12 font-medium"
        @click="clearAll"
      >
        {{ $t('KANBAN.ADVANCED_FILTERS.CLEAR_ALL') }}
      </button>
      <button
        class="text-sm bg-n-brand text-white font-semibold px-4 py-1.5 rounded-md transition-colors hover:brightness-110"
        @click="applyFilters"
      >
        {{ $t('KANBAN.ADVANCED_FILTERS.APPLY') }}
      </button>
    </div>
  </div>
</template>
